import toast from 'react-hot-toast'

// Check if browser supports notifications
export const isNotificationSupported = () => {
  return 'Notification' in window
}

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.warn('Notifications not supported in this browser')
    return false
  }

  try {
    const permission = await Notification.requestPermission()
    return permission === 'granted'
  } catch (error) {
    console.error('Error requesting notification permission:', error)
    return false
  }
}

// Check current permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'denied'
  return Notification.permission
}

// Show browser push notification
export const showPushNotification = (title, options = {}) => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    // Fallback to toast
    toast(title, { icon: '🔔' })
    return null
  }

  const notification = new Notification(title, {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    vibrate: [200, 100, 200],
    ...options
  })

  notification.onclick = () => {
    window.focus()
    notification.close()
    if (options.onClick) options.onClick()
  }

  return notification
}

// Show alert notification (combines push + toast)
export const showAlertNotification = (alert) => {
  const title = alert.title || 'New Alert'
  const body = alert.message || ''
  const priority = alert.priority || 'Medium'

  // Show toast based on priority
  const toastOptions = {
    duration: priority === 'Critical' ? 10000 : priority === 'High' ? 7000 : 5000,
    icon: priority === 'Critical' ? '🚨' : priority === 'High' ? '⚠️' : '🔔'
  }

  toast(`${title}${body ? ': ' + body : ''}`, toastOptions)

  // Also show push notification for high priority
  if (priority === 'Critical' || priority === 'High') {
    showPushNotification(title, {
      body,
      tag: alert._id || 'alert',
      requireInteraction: priority === 'Critical'
    })
  }
}

// Show reminder notification
export const showReminderNotification = (reminder) => {
  const title = reminder.title || 'Reminder'
  const body = reminder.description || reminder.message || ''

  // Toast notification
  toast(`⏰ ${title}${body ? ': ' + body : ''}`, { duration: 8000 })

  // Push notification
  showPushNotification(`⏰ ${title}`, {
    body,
    tag: reminder._id || 'reminder',
    requireInteraction: true
  })
}

// Show tender deadline reminder
export const showTenderDeadlineNotification = (tender) => {
  const daysLeft = Math.ceil((new Date(tender.deadline) - new Date()) / (1000 * 60 * 60 * 24))
  const title = `Tender Deadline Approaching`
  const body = `"${tender.title}" is due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`

  toast(`📅 ${title}: ${body}`, { duration: 8000 })

  if (daysLeft <= 3) {
    showPushNotification(title, {
      body,
      tag: `tender-${tender._id}`,
      requireInteraction: daysLeft <= 1
    })
  }
}

export default {
  isNotificationSupported,
  requestNotificationPermission,
  getNotificationPermission,
  showPushNotification,
  showAlertNotification,
  showReminderNotification,
  showTenderDeadlineNotification
}
